"use client";

import React from "react";

import Page, { useIsEditing } from "@/components/ui/page";
import Section from "@/components/ui/section";
import { cn, tinaFieldSafe, useTina } from "@/utils";
import Header from "@/components/Header";
import CallToAction from "@/components/CallToAction";
import Video from "@/components/Video";
import { Card, CardContent } from "@/components/ui/card";
import HubSpotMeetingsEmbed from "@/components/HubSpotMeetingsEmbed";
import LogosCarousel from "@/components/LogosCarousel";

export default function FormLandingPage(props) {
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  const page = data?.formLandingPages;
  const header = page?.header;
  const formSection = page?.formSection;
  const videoSection = page?.videoSection;
  const callToAction = page?.cta;
  const slug = page?._sys?.filename?.replace(".mdx", "") || "";

  const { isEditing, isOpen } = useIsEditing();
  const featuredVideo = videoSection?.video;
  const isHsFormEmbed = Boolean(
    formSection?.meetingEmbedUrl?.includes("share.hsforms.com") ||
      formSection?.meetingEmbedUrl?.includes(".hsforms.com"),
  );
  const formSizeClass =
    formSection?.formSize === "large"
      ? "[&>div:last-child]:lg:grid-cols-[1fr_760px]"
      : "[&>div:last-child]:lg:grid-cols-[1fr_640px]";

  return (
    <Page
      defaultTinaField={page}
      seo={page?.seo}
      pagePath={`/forms/${slug}`}
      titleFallback={page?.title || "Book a Demo"}
    >
      <Header
        {...header}
        variant="left"
        backgroundVariant="pixels-l-r"
        wrapperClassName={cn(
          "pb-40 md:pb-64 [&_h1]:lg:!text-5xl [&>div:last-child]:grid [&>div:last-child]:gap-8 [&>div:last-child]:grid-rows-[auto_1fr] [&>div]:lg:!gap-12 [&>div]:lg:!gap-y-16 [&>div]:lg:!gap-x-12 [&>div:last-child]:items-start [&>div:last-child]:min-w-0",
          formSizeClass,
        )}
      >
        <div
          className="relative row-span-2 max-md:order-2 min-w-0"
          data-tina-field={tinaFieldSafe(page, "formSection")}
        >
          <Card
            animateOnHover={false}
            wrapperClassName={cn(
              "!p-0 !py-0 md:!py-0 min-w-0",
              isHsFormEmbed
                ? "!h-auto overflow-hidden"
                : "h-auto !overflow-visible",
            )}
            backgroundVariant="default"
          >
            <CardContent
              className={cn(
                "max-md:pt-4 !px-3 min-w-0",
                isHsFormEmbed
                  ? "pb-3 pt-3 overflow-hidden"
                  : "pb-4 md:pb-0 overflow-visible md:-mb-3",
              )}
            >
              <div className={isHsFormEmbed ? "min-w-0 overflow-hidden" : "overflow-visible"}>
                <HubSpotMeetingsEmbed formSection={formSection} />
              </div>
            </CardContent>
          </Card>
          {isEditing && isOpen && (
            <div
              className="absolute inset-0 z-30 cursor-pointer"
              aria-hidden
            />
          )}
        </div>
        {header?.benefits && (
          <div className="space-y-6 max-md:order-1">
            {header?.benefits?.map((benefit, index: number) => (
              <div key={index}>
                <h3
                  className="h5"
                  data-tina-field={tinaFieldSafe(benefit, "title")}
                >
                  {benefit.title}
                </h3>
                <p data-tina-field={tinaFieldSafe(benefit, "description")}>
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        )}
      </Header>
      <LogosCarousel />
      <Section wrapperClassName="" className="space-y-8">
        <div className="space-y-3">
          <h2
            className="h3"
            data-tina-field={tinaFieldSafe(videoSection, "title")}
          >
            {videoSection?.title}
          </h2>
          <p data-tina-field={tinaFieldSafe(videoSection, "description")}>
            {videoSection?.description}
          </p>
        </div>
        {featuredVideo && (
          <Video
            video={featuredVideo}
            variant="card"
            size="md"
            animateOnHover={false}
            playInDialog={false}
          />
        )}
      </Section>
      <CallToAction
        {...callToAction}
        variant="left"
        backgroundVariant="dots"
      />
    </Page>
  );
}
